import type { TranslateFn } from '@/i18n/LanguageContext';
import { ApiError } from '@/api/client';
import type { ContractView, ContractStandingReason } from '@/api/types';

/** The status the operator reads: the stored state, except an ACTIVE row whose window does not contain today. */
export function contractStatusKey(c: Pick<ContractView, 'state' | 'standingReason'>): `contract.state.${string}` {
  if (c.state === 'ACTIVE' && (c.standingReason === 'EXPIRED' || c.standingReason === 'NOT_STARTED')) return `contract.state.${c.standingReason}`;
  return `contract.state.${c.state}`;
}

export function contractStatusTone(c: Pick<ContractView, 'standing' | 'state'>): 'ok' | 'warn' | 'danger' | 'neutral' {
  if (c.standing === 'ACTIVE') return 'ok';
  if (c.state === 'TERMINATED') return 'danger';
  if (c.state === 'DRAFT') return 'neutral';
  return 'warn';
}

export const REASON_KEYS: Record<ContractStandingReason, `onboard.reason.${ContractStandingReason}`> = {
  NONE: 'onboard.reason.NONE',
  DRAFT: 'onboard.reason.DRAFT',
  EXPIRED: 'onboard.reason.EXPIRED',
  SUSPENDED: 'onboard.reason.SUSPENDED',
  TERMINATED: 'onboard.reason.TERMINATED',
  NOT_STARTED: 'onboard.reason.NOT_STARTED',
};

/** One human sentence for a refused contract action - never a raw code. */
export function describeContractError(t: TranslateFn, err: unknown): string {
  if (!(err instanceof ApiError)) return t('contract.err.generic');
  switch (err.code) {
    case 'CONTRACT.TERMINATED': return t('contract.err.terminated');
    case 'CONTRACT.ACTIVATION_INCOMPLETE': return t('contract.err.activationIncomplete');
    case 'CONTRACT.ALREADY_ACTIVE': return t('contract.err.alreadyActive');
    case 'CONTRACT.NUMBER_TAKEN': return t('contract.err.numberTaken');
    case 'CONTRACT.DOCUMENT_INVALID': return t('contract.err.document');
    case 'MEMBERSHIP.OUT_OF_SCOPE': return t('contract.err.outOfScope');
    case 'COMMON.VALIDATION_FAILED':
      return err.params.field === 'validTo' ? t('contract.err.period') : t('contract.err.generic');
    default:
      if (err.status === 403) return t('contract.err.forbidden');
      if (err.status === 404) return t('contract.err.notFound');
      if (err.status === 413) return t('contract.err.document');
      return t('contract.err.generic');
  }
}
